import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { Goal } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import type { ListResponse } from '@feathr/rachis';
import { isAuthenticatedStore, useStore } from '@feathr/report_components/state';

/**
 * Parameters for the useGoalsLoadStatus hook.
 */
interface IUseGoalsLoadStatusParams {
  /** Whether the report is configured to include goals. */
  hasGoals: boolean;
  /** The goals list response from the store. */
  goals: ListResponse<Goal>;
}

/**
 * Return value for the useGoalsLoadStatus hook.
 */
interface IUseGoalsLoadStatusReturn {
  /** Whether goals have been successfully loaded and are available for display. */
  hasLoadedGoals: boolean;
}

/**
 * Determines whether conversion goals loaded successfully and shows a warning
 * toast when they fail in an authenticated context.
 *
 * Shared between EventReport and FlightReport to avoid duplicating
 * error-handling logic for goal loading failures.
 *
 * @param params.hasGoals - Whether the report is configured to include goals.
 * @param params.goals - The goals list response from the store.
 * @returns An object containing `hasLoadedGoals`.
 *
 * @example
 * ```tsx
 * const { hasLoadedGoals } = useGoalsLoadStatus({ hasGoals, goals });
 * ```
 */
function useGoalsLoadStatus({
  hasGoals,
  goals,
}: Readonly<IUseGoalsLoadStatusParams>): IUseGoalsLoadStatusReturn {
  const store = useStore();
  const { t } = useTranslation();

  const hasLoadedGoals = hasGoals && !goals.isErrored && goals.models.length > 0;

  useEffect(() => {
    if (goals.isErrored && isAuthenticatedStore(store)) {
      toast(
        t('Unable to load conversion goals. The report is shown without ROI and conversion data.'),
        { type: 'warning' },
      );
    }
  }, [goals.isErrored, store, t]);

  return { hasLoadedGoals };
}

export { useGoalsLoadStatus };
export type { IUseGoalsLoadStatusParams, IUseGoalsLoadStatusReturn };
